import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import api, { KanbanTicket } from '../services/api';

// Pick the tech, the day and the time for one ticket. Works by touch, so it is the way to assign on the phone,
// where the Dashboard opens inside the staff app. Times are sent as local wall clock, the same as the Calendar page.
interface Tech { id: number; name?: string; full_name?: string; first_name?: string; last_name?: string; }
const pad = (n: number) => String(n).padStart(2, '0');
const dayOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const techName = (t: Tech) => t.name || t.full_name || [t.first_name, t.last_name].filter(Boolean).join(' ') || `Tech ${t.id}`;

export default function AssignTicketDialog({ ticket, onClose, onDone }: {
  ticket: KanbanTicket | null; onClose: () => void; onDone: (message: string) => void;
}) {
  const [techs, setTechs] = useState<Tech[]>([]);
  const [techId, setTechId] = useState<number | ''>('');
  const [day, setDay] = useState(dayOf(new Date()));
  const [time, setTime] = useState('08:00');
  const [minutes, setMinutes] = useState(60);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ticket) return;
    setTechId(''); setError(''); setBusy(false); setMinutes(60);
    const s = ticket.scheduled_start ? new Date(ticket.scheduled_start) : null;
    if (s && !isNaN(s.getTime())) { setDay(dayOf(s)); setTime(`${pad(s.getHours())}:${pad(s.getMinutes())}`); }
    else { setDay(dayOf(new Date())); setTime('08:00'); }
    api.get('/technicians').then((r: any) => setTechs(r.data || [])).catch(() => setError('Could not load the technicians.'));
  }, [ticket]);

  const save = async () => {
    if (!ticket) return;
    const need = [!techId && 'Technician', !day && 'Day', !time && 'Start time'].filter(Boolean);
    if (need.length) { setError(`Please fill in: ${need.join(', ')}`); return; }
    setBusy(true); setError('');
    const [h, m] = time.split(':').map(Number);
    const [y, mo, d] = day.split('-').map(Number);
    const end = new Date(y, mo - 1, d, h, m + (minutes > 0 ? minutes : 60));
    try {
      await api.post(`/tickets/${ticket.id}/assign`, {
        assigned_to_id: techId,
        scheduled_start: `${day}T${time}:00`,
        scheduled_end: `${dayOf(end)}T${pad(end.getHours())}:${pad(end.getMinutes())}:00`,
      });
      const who = techs.find((t) => t.id === techId);
      onDone(`${ticket.ticket_number} assigned to ${who ? techName(who) : 'the tech'}`);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'The ticket could not be assigned.');
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!ticket} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Assign {ticket?.ticket_number}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {ticket?.title}{ticket?.customer_name ? ` · ${ticket.customer_name}` : ''}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField select label="Technician" value={techId} onChange={(e) => setTechId(Number(e.target.value))} fullWidth>
            {techs.map((t) => <MenuItem key={t.id} value={t.id}>{techName(t)}</MenuItem>)}
          </TextField>
          <TextField type="date" label="Day" value={day} onChange={(e) => setDay(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField type="time" label="Start time" value={time} onChange={(e) => setTime(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField type="number" label="Length in minutes" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} inputProps={{ min: 15, step: 15 }} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy}>{busy ? 'Assigning…' : 'Assign'}</Button>
      </DialogActions>
    </Dialog>
  );
}
